<!--

Copyright Paul B. Lucas (C) 2023, 2024   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered only when the user has a propertity 'is-sys-admin' and ius used to manage
configurations.

Revisions:
    02/25/2025      pblucas     created view
 

-->
<template>
    <div class="_master_container" >

        <!-- MARK: Present user with dropdowns to select out code or enter orgcode -->
        <div class="_info" v-if="sRenderState == 'SELECT-ORG'">
            <h1 class="_h1">Per Orgcode Web Application Configuration</h1>
            <p class="_p_info">
                This page allows the creation or editing of a configuration sheet which
                may be applied to a specific orgcode.  A configuration sheet allows the 
                web application to be customized to some extent.  Customizations include: </p>
            <ul class="_ul">
                    <li class="_li">Whether employee photos are to be suppressed</li>
                <li class="_li">Setting of a customized legal notice</li>
                <li class="_li">Setting a unit title</li>
                <li class="_li">Setting a unit URL for linking purposes</li>
            </ul>
            
            <div class="_org_selector" >
                <p class="_p_info">
                    The configuration may be applied to department level or college level by excluding a 
                    specific org code.  Configuration applicability is based on most specific (orgcode) to 
                    least specific (college-code), e.g., an orgcode configuration overrides a college level 
                    configuration. Configuration changes for approvers will update any agreements on file.
                </p>
                <OrgSelector  @setCodes="applyCodes"/> 
            </div>  
        </div> 
        <!-- MARK: Notification that no config sheet exists - options to create one -->
        <div class="_info" v-if="sRenderState == 'NOT-FOUND'">
            <br/>
            <p class="_p_red">
                A configuration sheet was not found for the collge, department, or org as
                specified  {{ invalidCode }}.  You can try to respecify or create a new
                configuration sheet based on the selected org, department, or college.
            </p>
            <br /><br/>

            <div class="_button_container">
                <button class="_main_button" 
                    tabindex="0"    
                    aria-label="Create a new config sheet" 
                    aria-describedby="Control to create new config sheet when entered orgcode invalid" 
                    @click="showForm" 
                    @keydown.enter="ShowForm">Create New Config Sheet</button>
                <button class="_main_button" 
                    tabindex="0" 
                    aria-label="Go back to org selector" 
                    aria-describedby="Return to org selector to respecify an affiliation" 
                    @click="resetForm"
                    @keydown.enter="resetForm">Respecify</button>
           </div>
        </div>

        <!-- MARK: Notification that new config was saved -->
        <div class="_info" v-if="sRenderState == 'CONFIG-SAVED'">
            <p class="_p_info">
                A configuration sheet has been saved for {{ configDisplay }}.  Any user having a home-org, 
                college, department, or division orgcode within context of the configuration Sheet
                will be used when the user is employing the application.  
            </p>

            <div class="_button_container">
                <button class="_main_button" 
                    tabindex="0" 
                    aria-label="Control to reset and respecify affiliation" 
                    aria-describedby="Control to return to affiliation selection" 
                    @click="resetForm"
                    @keydown.enter="resetForm">Specify Another Config</button>
           </div>
        </div>


        <!-- MARK:  Full page edit view of configuration sheet -->
        <div class="_display_container" v-if="sRenderState == 'SHOW-CONFIG'">
            <h1 class="_h1_02">Configuration Sheet for {{ configDisplay }}</h1>
            <p v-if="bUsingDefaultConfig" class="_p_config">Using default configuration - not in database</p>
            <div class="_row_container">
                <!-- establish the basic org codes to which this configuration applies - will be set by selector -->  
                <div class="_col_50" style="order: 1;">
                    <p class="_p_section">Configuration Applied To</p>
                    <div class="_row_box">
                        <div class="_row">
                            <div class="_scol_40">
                                <label class="_item_label" for="prop_4">Select College Code</label>
                            </div>
                            <div class="_scol_60">
                                <select id="prop_4" v-model="workingConfig['apply-college-code']" class="_select"  
                                    tabindex="0"
                                    aria-label="college code dropdown selector" 
                                    aria-describedby="dropdown seletor for college names and codes"
                                    @change="updateCollegeCode" autofocus  >
                                    <!-- item['code'] is college code, item ['name'] is name of college -->
                                    <!-- <option id="cc" class="_opt_list" disabled>{{ workingConfig['apply-college-code'] }} - {{ findCollegeName(workingConfig['apply-college-code']) }}</option> -->
                                    <option id="cc" class="_opt_list" v-for="(item, idx) in aCollegeCodes" :value="item['code']"> {{item['code']}} - {{ item['name'] }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="_row" v-if="bShowDeptSelector">
                            <div class="_scol_40">
                                <label class="_item_label" for="prop_5">Select Department Code</label>
                            </div>
                            <div class="_scol_60">
                                <select id="prop_5" v-model="workingConfig['apply-dept-code']" class="_select" 
                                    tabindex="0" 
                                    aria-label="department code dropdown selector"
                                    aria-describedby="dropdown selector for department names and codes within selected college"
                                    @change="updateDeptCode" autofocus  >
                                    <!-- item['code'] is college code, item ['name'] is name of college -->
                                    <!-- <option id="cc" class="_opt_list" disabled>{{ workingConfig['apply-dept-code'] }}</option> -->
                                    <option id="cc" class="_opt_list" v-for="(item, idx) in aFilteredDeptCodes" :value="item['dept-code']"> {{item['dept-code']}} - {{ item['name'] }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="_row" v-if="bShowOrgSelector">
                            <div class="_scol_40">
                                <label class="_item_label" for="prop_6">Select Orgcode</label>
                            </div>
                            <div class="_scol_60">
                                <select id="prop_6" v-model="workingConfig['apply-orgcode']" class="_select"  
                                    tabindex="0" 
                                    aria-label="division or ogrcode dropdown selector" 
                                    aria-describedby="dropdown selector for division names/orgcodes withn a selected department" 
                                    @change="updateOrgCode" autofocus  >
                                    <!-- item['code'] is college code, item ['name'] is name of college -->
                                    <!-- <option id="cc" class="_opt_list" disabled>{{ workingConfig['apply-orgcode'] }}</option> -->
                                    <option id="cc" class="_opt_list" v-for="(item, idx) in aFilteredOrgCodes" :value="item['org']"> {{item['org']}} - {{ item['name'] }}</option>
                                </select>
                            </div>
                        </div>  
                    </div>    
                </div>

                <!-- establish per-unit policies on photos and authentication of approvers -->
                <div class="_col_50" style="order: 2;">
                    <p class="_p_section">Per Unit Policies </p>
                    <p class="_p_diminished_grn_2">These presets are applied on a per-orgcode basis.</p>                   
                    <div class="_row_box">
                        <div class="_row">
                            <p class="_p_diminished_grn">If checked, all employees in org will have photos suppressed.</p> 
                            <div class="_scol_8">
                                <input id="prop-7" class="_check" type="checkbox" v-model="workingConfig['suppress-photos']"  
                                    tabinsex="0"
                                    aria-label="checkbox for policy to suppress all photos" 
                                    aria-describedby="when checked suppresses all employee photos for pages in unit" 
                                    value=true>
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label" for="prop-7">Suppress all employee photos</label>
                            </div>
                        </div>
                        <div class="_row">
                            <p class="_p_diminished_grn_2">If checked, mandatory audit data on forms before submittal is suppressed.</p> 
                            <div class="_scol_8">
                                <input id="prop-7.1" class="_check" type="checkbox" v-model="workingConfig['suppress-cmdb-data']"  
                                    tabinsex="0"
                                    aria-label="checkbox for policy to suppress cmdb data" 
                                    aria-describedby="when checked suppresses all cmdb data on writes in unit" 
                                    value=true>
                            </div>
                            <div class="_scol_80">
                                <label class="_item_label" for="prop-7.1">Suppress CMDB data</label>
                            </div>
                        </div>
                        <div class="_row">
                            <p class="_p_diminished_grn_2">Select Code128 unless alternate required for legacy systems.</p> 
                            <div class="_scol_40">
                                <label class="_item_label" for="prop_7.2">Tag Barcode Format</label>
                            </div>
                            <div class="_scol_60">
                                <select id="prop_7.2" v-model="workingConfig['barcode-type']" class="_select"  
                                    tabindex="0" 
                                    aria-label="barcode format selector" 
                                    aria-describedby="dropdown selector for barcode formats for a selected department" 
                                    autofocus  >
                                    <option class="_opt_list" v-for="(item, idx) in aBarcodeTypes" :value="item"> {{ item }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="_row">
                            <p class="_p_diminished_grn_2">Enter 2-character alpha prefix for autogenerated asset tag number sequences.</p> 
                            <div class="_scol_40">
                                <label class="_item_label" for="prop_8">Tag AutoGen Prefix</label>
                            </div>
                            <div class="_scol_25">
                                <input id="prop_8" v-model="workingConfig['tag-prefix']" class="_input"  
                                    tabindex="0" 
                                    aria-label="asset number tag prefix" 
                                    aria-describedby="input for two-character tag prefix" 
                                    autofocus  >                                    
                            </div>
                        </div>

                    </div>
                        
                </div>
            </div>

            <!-- set up the legal notice and web application branding title -->
            <div class="_row_container">
                <div class="_col_50" style="order: 1;">
                    <p class="_p_section">Legal Notice</p>
                    <p class="_p_diminished_grn">If this field is left blank, the notice box will not appear on the home page.</p>
                    <div class="_row_box">           
                        <textarea id="prop-10" class="_textarea" autocorrect="on" cols="30" 
                            tabindex="0"
                            aria-label="text area input"  
                            aria-decribedby="enter legal notice that appears on home page" 
                            v-model="workingConfig['notice']">
                            {{ workingConfig['notice'] }}
                        </textarea>
                    </div>         
                </div>   
                <div class="_col_50" style="order: 2;">
                    <p class="_p_section">Web Application Branding Title</p>
                    <p class="_p_diminished_grn">Title appears on headers above the application name with link.</p>
                    <div class="_row_box">
                        <div class="_row">
                            <div class="_scol_15">
                                <label class="_item_label" for="prop-11">Title</label>  
                            </div>
                            <div class="_scol_80">
                                <input id="prop-11" class="_input" type="text" 
                                    tabindex="0" 
                                    aria-label="input for title" 
                                    aria-describedby="input for text which appears on header of each page used for unit branding" 
                                    v-model="workingConfig['title']">
                            </div>
                        </div>  
                        <div class="_row">
                            <div class="_scol_15">
                                <label class="_item_label" for="prop-11">Title Link</label>  
                            </div>
                            <div class="_scol_80">
                                <input id="prop-11" class="_input" type="text" 
                                    tabindex="0" 
                                    aria-label="input for title link" 
                                    aria-describedby="input for link which is attached to title" 
                                    v-model="workingConfig['title-link']">
                            </div>
                        </div>  
                    </div>                    
                </div>              
            </div>
            <div class="_button_container">
                <button class="_main_button" 
                    tabindex="0" 
                    aria-label="action to store edited config sheet" 
                    aria-describedby="stores configuration sheed and updated unit agreements with new data" 
                    @click="saveConfig"
                    @keydown.enter="saveConfig">Store Config</button>
                <button class="_main_button" 
                    tabindex="0" 
                    aria-label="action to reset form and cancel any changes" 
                    arial-describedby="exists configuration sheed and returns to affiliation selector"
                    @click="resetForm"
                    @keydown.enter="resetForm">Reset Form</button>
            </div>
        </div>
    </div>

</template>

<script>
    import {ref, computed}      from 'vue'
    import { useStore }         from 'vuex'
    import OrgSelector          from '@/components/GeneralUse/OrgSelector.vue'
    import useConfigAPI         from '@/composables/useConfigAPI'
    import useRefreshAPI        from '@/composables/useRefreshAPI'
    import usePermissionsAPI    from '@/composables/usePermissionsAPI'  
    import useArchiBusAPI       from '@/composables/useArchiBusAPI'

    import Options              from '@/configuration/form-options.json'   

    //import uiucCodes            from '@/configuration/uiuc.codes.json'

    export default {
        name: 'home',
        components: {
            OrgSelector        
        },
        props: [
            'claims'
        ],

        emits: [            
        ],
    
        setup(props)    {

            const store = useStore()
            const { uiucCodeResults, makeUIUCCodes } = useArchiBusAPI()
            const { checkStoreForRefresh } = useRefreshAPI()
            const { checkAdminGroups } = usePermissionsAPI()

            const user              = computed( () => store.state.user)
            const config            = computed( () => store.state.config)
 
            // Vuex store actions (strict mode)
            const setConfig         = (config)   => store.dispatch('setConfig', config)
    
            const configDisplay         =   ref("")
            const invalidCode           =   ref("")
            const bUsingDefaultConfig   =   ref(false)

            // will be set by ArchiBus API call
            const uiucCodes             =   ref({})
            const bSortByName           =   ref(false)   // true sorts listed by 'name' | false by 'code';
            const aCollegeCodes         =   ref([])
            const aDeptCodes            =   ref([])      // name[0], college-code[1], dept-code[2]
            const aOrgCodes             =   ref([])      // name[0], dept-code[1], org[2]

            // other config props
            const aBarcodeTypes         =   ref([])
            aBarcodeTypes.value         =   Options['barcode-types']

            makeUIUCCodes()
            .then ( () => {
                uiucCodes.value = uiucCodeResults.value

                // select list 
                aCollegeCodes.value         =   uiucCodes.value['college-codes']
                aDeptCodes.value            =   uiucCodes.value["dept-codes"]             // name[0], college-code[1], dept-code[2]
                aOrgCodes.value             =   uiucCodes.value["org-codes"]              // name[0], dept-code[1], org[2]

                // sort
                aCollegeCodes.value.sort((a, b) => {
                    var codeA
                    var codeB
                    if(bSortByName.value) {
                        codeA = a['name']
                        codeB = b['name']
                    }
                    else {
                        codeA = a['code']
                        codeB = b['code']
                    }                        
                    if (codeA < codeB) {
                        return -1
                    }
                    if (codeA > codeB) {
                        return 1
                    }
                    // codes must be equal
                    return 0
                });

            })

            // MARK:  Because configs in store cannot be mutated directly (strict rules)
            //        local variables are set on form and then passed to methods which call
            //        config mutators
            //
            //  In general, amy config here is on a affiliation which is being established
            //  and not necessarily on the user's affiliation.  Howeve, if the user's 
            //  affilitiated config is altered, the the store config must also be mutated.
            //
            // The following is a default config sheet
            const workingConfig     =   ref({
                'apply-college-code':       null,
                'apply-dept-code':          null,
                'apply-orgcode':            null,
                'notice':                   "",
                'title':                    "University of Illinois Urbana-Champaign",
                'title-link':               "https://illinois.edu",
                'suppress-photos':          false,
                'suppress-cmdb-data':       false,
                'barcode-type':             "CODE128",
                'tag_prefix':               "UN"
            })

            // MARK:  refresh store from database if global browser refresh fubars the store
            checkStoreForRefresh()
            checkAdminGroups(props.claims, store)

 
            // filtered values for selection process
            const aFilteredDeptCodes    =   ref([])
            const aFilteredOrgCodes     =   ref([])

            // use the folowing in this. context
            return {user, workingConfig, configDisplay, config, aBarcodeTypes,
                    aCollegeCodes, aDeptCodes, aFilteredDeptCodes, aOrgCodes,
                    aFilteredOrgCodes, setConfig, invalidCode, bUsingDefaultConfig   
                }
        },

        data() {
            return {
                sRenderState: 'SELECT-ORG',
                bShowDeptSelector: true,
                bShowOrgSelector: true         
            }
        },

        // hooks
        beforeMount() {

        },
        mounted() {
            //console.log("Settings:: mounted: workingConfig['apply-college-code']"+this.workingConfig['apply-college-code'])
            //console.log("Settings:: mounted: workingConfig['apply-dept-code']"   +this.workingConfig['apply-dept-code'])
            //console.log("Settings:: mounted: workingConfig['apply-orgcode']"     +this.workingConfig['apply-orgcode'])

        },
        updated() {
            //console.log("Settings:: updated: (this user) config: "+JSON.stringify(this.config))
            //console.log("Settings:: updated: workingConfig['apply-dept-code']"   +this.workingConfig['apply-dept-code'])
            //console.log("Settings:: updated: workingConfig['apply-orgcode']"     +this.workingConfig['apply-orgcode'])


        },
        methods:    {

            // FUNCTION:  convienence conversion for title of sheet
            // Note that workingConfig will have department and orgcode set as
            // any ambiguous orgcode will be resolved in OrgSelector component
            // However, must resolve college, division, and department for string
            // Returns display string
            //
            makeConfigDisplay()  {
                // logic to detail what the set applies to (for display only)
                // sets configDisplay with names of college, department, and division
                let configDisplay   =   ""
                let college         =   ""
                let department      =   ""
                let division        =   ""
                let divcode         =   ""

                // intermediates
                let sCollegeCode    =   ""
                let sDeptCode       =   ""

                // filter result arrays
                let aOrgList        =   []
                let aDeptList       =   []
                let aCollegeList    =   []
   
                // MARK:  We may have either a valid unique orgcode, or a valid department, 
                //        or a nonunique orgcode disambiguated by department
                //        We want to display the entire affiliation, but if at department level note
                //        all orgs, and if college denote all department/all orgs
                //          
                //      List formats: aOrgCodes     name[0], dept-code[1], org[2]
                //                    aDeptCodes    name[0], college-code[1], dept-code[2]
                //                    aCollegeCodes name[0], code[1]
                //
                // Assume disambiguation on entry
                // error case - no codes set     
                if(    (this.workingConfig['apply-orgcode'] === null)
                    && (this.workingConfig['apply-dept-code'] === null)
                    && (this.workingConfig['apply-college-code'] === null ) ) {
                    // error condition that can occur if orgSelector return nothing
                    configDisplay = "default configuration"
                    return configDisplay
                } 
                // case 1: unique orgcode
                else if(    (this.workingConfig['apply-orgcode'] !== null)
                    && (this.workingConfig['apply-dept-code'] === null)
                    && (this.workingConfig['apply-college-code'] === null ) ) {
                    divcode =   this.workingConfig['apply-orgcode']
                    // need to resolve names for college, department, and division
                    // returns array
                    aOrgList = this.aOrgCodes.filter( (item) =>    {
                            return item['org'] == this.workingConfig['apply-orgcode']
                    })
                    sDeptCode   =   aOrgList[0]['dept-code']
                    division    =   aOrgList[0]['name']
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== sDeptCode
                    })
                    sCollegeCode = aDeptList[0]['college-code']
                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== sCollegeCode
                    })
                    college = aCollegeList[0]['name']
                }
                // case 2: non-unique orgcode disambiguated with department code 
                else if(    (this.workingConfig['apply-orgcode'] !== null)
                        && (this.workingConfig['apply-dept-code'] !== null)
                        && (this.workingConfig['apply-college-code'] === null) ) {

                    divcode =   this.workingConfig['apply-orgcode']

                    // need to resolve names for college, department, and division
                    // returns array - do dept first to provide key for disambiguation of org list
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    department      =   aDeptList[0]['name']
                    sCollegeCode    =   aDeptList[0]['college-code']
                    aOrgList = this.aOrgCodes.filter( (item) =>    {
                            return (   item['org'] == this.workingConfig['apply-orgcode']
                                    && item['dept-code'] == this.workingConfig['apply-dept-code'])   
                    })
                    division        =   aOrgList[0]['name']                 
                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== sCollegeCode
                    })
                    college = aCollegeList[0]['name']

                }
                // case 3: department only
                else if(    (this.workingConfig['apply-orgcode'] === null)
                        && (this.workingConfig['apply-dept-code'] !== null)
                        && (this.workingConfig['apply-college-code'] === null) ) {
                        
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    divcode         =   null
                    department      =   aDeptList[0]['name']
                    sCollegeCode    =   aDeptList[0]['college-code']
                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== sCollegeCode
                    })
                    college = aCollegeList[0]['name']
                    division = 'all orgs'
                }
                // case 3-1: department only but selected with college
                else if(    (this.workingConfig['apply-orgcode'] === null)
                        && (this.workingConfig['apply-dept-code'] !== null)
                        && (this.workingConfig['apply-college-code'] !== null) ) {
                        
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    divcode         =   null
                    department      =   aDeptList[0]['name']
                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== this.workingConfig['apply-college-code']
                    })
                    college = aCollegeList[0]['name']
                    division = 'all orgs'
                }
                // case 4: college only
                else if(    (this.workingConfig['apply-orgcode'] === null)
                        && (this.workingConfig['apply-dept-code'] === null)
                        && (this.workingConfig['apply-college-code'] !== null) ) {

                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== this.workingConfig['apply-college-code']
                    })
                    college = aCollegeList[0]['name']
                    department  =   'all departments'
                    division    =   'all orgs'
                    divcode     =   null

                }
                // default case
                else if(    (this.workingConfig['apply-orgcode'] !== null)
                        && (this.workingConfig['apply-dept-code'] !== null)
                        && (this.workingConfig['apply-college-code'] !== null) ) {
                    
                    divcode     =   this.workingConfig['apply-orgcode']
                    aOrgList = this.aOrgCodes.filter( (item) =>    {
                        return item['org'] == this.workingConfig['apply-orgcode']
                    })
                    division    =   aOrgList[0]['name']
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    department      =   aDeptList[0]['name']
                    aCollegeList = this.aCollegeCodes.filter( (item) => {
                        return item['code']== this.workingConfig['apply-college-code']
                    })
                    college = aCollegeList[0]['name']
                }

                // build the display string
                configDisplay  = college+', '
                configDisplay += department+', '
                configDisplay += division   
                if(divcode) configDisplay += " ("+divcode+")"   
                return configDisplay  
             
            },

            // FUNCTION: to apply selected codes from selector to workingConfig
            // then fetch possible configs on selector results
            // Note that store.state.config is not affected as we are creating/editing configs - not using them
            //
            //  Codes:  'college-code'
            //          'dept-code'
            //          'orgcode'
            //
            //  Configs may be stored on any of the above, the others null or filled in.  Take most specific
            //  by selecting on least to most inclusive codes 
            //
            //  NOTE:  Entry of orgcode in OrgSelector as direct input is disambiguated.  
            //  department code is supplied as disambiguation agent in codes, therefor no need to consider
            //  orgcode disambiguation here
            //
            applyCodes(codes)   {
                
                const {configResults, fetchConfigsOnQuery } = useConfigAPI()

                // intermediates initilization
                let sDeptCode       =   ""
                let sCollegeCode    =   ""

                // filter result arrays initialization
                let aOrgList        =   []
                let aDeptList       =   []
     
                // set a flag controlling query generation
                let sProcessOn  =   'false'

                // MARK:  Determining how to fetch configs - disambiguation performed at orgSelector
                // which means that codes will never be set alone unless it is unique

                //console.log("Settings:: applyCodes: codes: "+JSON.stringify(codes))

                // case 1: unique org code - find department as required for database 
                if(    (codes['orgcode'] !== null)
                    && (codes['dept-code'] === null) 
                    && (codes['college-code'] === null) ) {

                    //console.log("Settings:: applyCodes: processing case 1 - unique org")

                    aOrgList = this.aOrgCodes.filter( (item) =>    {
                            return item['org'] == codes['orgcode']
                    })
                    sDeptCode   =   aOrgList[0]['dept-code']
                    this.workingConfig['apply-orgcode']         = codes['orgcode']
                    this.workingConfig['apply-dept-code']       = sDeptCode

                    // fill in college code for selection display
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    sCollegeCode    =   aDeptList[0]['college-code']
                    this.workingConfig['apply-college-code']       = sCollegeCode
                    sProcessOn = 'org-dept'
                }
                // case 2: disambiguated org code with department  
                else if(    (codes['orgcode'] !== null)
                         && (codes['dept-code'] !== null) 
                         && (codes['college-code'] === null)) {

                    //console.log("Settings:: applyCodes: processing case 2 - disambiguated org with department")

                    this.workingConfig['apply-orgcode']    = codes['orgcode']
                    this.workingConfig['apply-dept-code']  = codes['dept-code']

                    // fill in college code for selection display
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    sCollegeCode    =   aDeptList[0]['college-code']
                    this.workingConfig['apply-college-code']       = sCollegeCode               
                    sProcessOn = 'org-dept'   
                }
                // case 3: department only
                else if(    (codes['orgcode'] === null)
                         && (codes['dept-code'] !== null) 
                         && (codes['college-code'] === null)) {

                    //console.log("Settings:: applyCodes: processing case 3 - department only")

                    this.workingConfig['apply-dept-code'] = codes['dept-code']

                    // fill in selector for college
                    aDeptList = this.aDeptCodes.filter( (item) => {
                        return item['dept-code']== this.workingConfig['apply-dept-code']
                    })
                    sCollegeCode    =   aDeptList[0]['college-code']
                    this.workingConfig['apply-college-code']       = sCollegeCode
                    sProcessOn = 'dept'   
                }
                // case 3-1: department only but with college selector
                else if(    (codes['orgcode'] === null)
                         && (codes['dept-code'] !== null) 
                         && (codes['college-code'] !== null)) {

                    //console.log("Settings:: applyCodes: processing case 3.1 - dept with college selector")

                    this.workingConfig['apply-dept-code']       = codes['dept-code']
                    this.workingConfig['apply-college-code']    = codes['college-code']
                    sProcessOn = 'dept'   
                }
                // case 4: college code only
                else if(    (codes['orgcode'] === null)
                         && (codes['dept-code'] === null) 
                         && (codes['college-code'] !== null)) {

                    //console.log("Settings:: applyCodes: processing case 4 - college only")

                            
                    this.workingConfig['apply-college-code'] = codes['college-code']
                    sProcessOn = 'college'   
                }
                
                // case 5: all affiliation codes entered
                else if(    (codes['orgcode'] !== null)
                         && (codes['dept-code'] !== null) 
                         && (codes['college-code'] !== null)) {

                    //console.log("Settings:: applyCodes: processing case 5 - full selection")
       
                    this.workingConfig['apply-college-code']    = codes['college-code']
                    this.workingConfig['apply-dept-code']       = codes['dept-code']
                    this.workingConfig['apply-orgcode']         = codes['orgcode']
                    sProcessOn = 'all'   
                }

                //console.log("Settings: applyCodes: workingConfig: college: "+this.workingConfig['apply-college-code']+
                //            ", dept: "+this.workingConfig['apply-dept-code']+", org: "+this.workingConfig['apply-orgcode'])

                // MARK:  Build a query statement for the config lookup in database
                var cQuery = {}
                switch(sProcessOn) {
                    case 'false':
                        console.log("Settings:  code selection invalid")
                        this.sRenderState = 'SELECT-ORG'
                        break;

                    case 'org-dept':
                        cQuery['apply-department-code'] =   this.workingConfig['apply-dept-code']
                        cQuery['apply-orgcode']         =   this.workingConfig['apply-orgcode'] 
                        break;
                        
                    case 'dept':
                        cQuery['apply-department-code'] =   this.workingConfig['apply-dept-code']
                        break;
                    
                    case 'college':
                        cQuery['apply-college-code']    =   this.workingConfig['apply-college-code']
                        break;  
                        
                    case 'all':
                        cQuery['apply-college-code']    =   this.workingConfig['apply-college-code']
                        cQuery['apply-department-code'] =   this.workingConfig['apply-dept-code']
                        cQuery['apply-orgcode']         =   this.workingConfig['apply-orgcode'] 
                        break;
                }

                fetchConfigsOnQuery(cQuery) 
                .then( () => {
                    if(configResults.value.length > 0) {
                        // config found on query 
                        this.workingConfig = configResults.value[0]
                        this.bUsingDefaultConfig = false
                    }
                    else {
                        // set default workingConfig application codes values
                        // values can be null
                        this.workingConfig['apply-orgcode']         = codes['orgcode']
                        this.workingConfig['apply-dept-code']       = codes['dept-code']
                        this.workingConfig['apply-college-code']    = codes['college-code']
                        this.bUsingDefaultConfig = true
                    }

                    // config is read from database but may be stored as only department or as only college
                    // Storage of configs:
                    //      org + dept
                    //      dept
                    //      college
                    // Additionally default is going to be set by OrgSelector codes
                    // In either case, attempt to fill in upper level codes for selectors as needed

                    
                    // case 1: org+dept set - fill in college
                    if(    (this.workingConfig['apply-orgcode'] !== null)
                        && (this.workingConfig['apply-dept-code'] !== null) 
                        && (this.workingConfig['apply-college-code'] === null) ) {

                        // fill in college code for selection display
                        aDeptList = this.aDeptCodes.filter( (item) => {
                            return item['dept-code']== this.workingConfig['apply-dept-code']
                        })
                        sCollegeCode    =   aDeptList[0]['college-code']
                        this.workingConfig['apply-college-code']       = sCollegeCode

                        // enable detailed display of selected items
                        this.updateCollegeCode()
                        this.updateDeptCode()
                        this.bShowDeptSelector  =   true
                        this.bShowOrgSelector   =   true                   
                    }
                    // case 2: department only - config applied to entire department
                    else if(    (this.workingConfig['apply-orgcode'] === null)
                             && (this.workingConfig['apply-dept-code'] !== null) 
                             && (this.workingConfig['apply-college-code'] === null) ) {

                        // fill in college code for selection display
                        aDeptList = this.aDeptCodes.filter( (item) => {
                            return item['dept-code']== this.workingConfig['apply-dept-code']
                        })
                        sCollegeCode    =   aDeptList[0]['college-code']
                        this.workingConfig['apply-college-code']       = sCollegeCode

                        // enable detailed display of selected items
                        this.updateCollegeCode()
                        this.updateDeptCode()
                        this.bShowDeptSelector  =   true
                        this.bShowOrgSelector   =   false                   
                    }
                    // case 3: college only -config applied to entire college
                    else if(    (this.workingConfig['apply-orgcode'] === null)
                             && (this.workingConfig['apply-dept-code'] === null) 
                             && (this.workingConfig['apply-college-code'] !== null) ) {

                        // enable detailed display of selected items
                        this.updateCollegeCode()
                        this.updateDeptCode()
                        this.bShowDeptSelector  =   true
                        this.bShowOrgSelector   =   false                   
                    }                    
                    // case 3-1: college and department set  -config applied to entire department
                    else if(    (this.workingConfig['apply-orgcode'] === null)
                             && (this.workingConfig['apply-dept-code'] !== null) 
                             && (this.workingConfig['apply-college-code'] !== null) ) {

                        // enable detailed display of selected items
                        this.updateCollegeCode()
                        this.updateDeptCode()
                        this.bShowDeptSelector  =   true
                        this.bShowOrgSelector   =   false                   
                    }
                    // case 4: all codes set
                    else if(    (this.workingConfig['apply-orgcode'] !== null)
                             && (this.workingConfig['apply-dept-code'] !== null) 
                             && (this.workingConfig['apply-college-code'] !== null) ) {
                        
                        // enable detailed display of selected items
                        this.updateCollegeCode()
                        this.updateDeptCode()
                        this.bShowDeptSelector  =   true
                        this.bShowOrgSelector   =   true                   
                    } 
                    this.configDisplay = this.makeConfigDisplay()
                    this.sRenderState = 'SHOW-CONFIG'
                })
            },

            async saveConfig() {
                //console.log("Settings:: createConfig fired")

                const {configResults, configKeyData, fetchConfigsOnQuery, storeConfig} = useConfigAPI()

                //console.log( "settings.vue:: working config: "+JSON.stringify(this.workingConfig))

                await storeConfig(this.workingConfig)
                .then( async () => {
                    await fetchConfigsOnQuery({'id': configKeyData.value[0].pkey})
                    .then( async () => {

                        // Determine if this config applies to the current user.
                        // Remember to trin home-org (6 digits to 3)
                        if (this.workingConfig['apply-orgcode'] == this.user['home-org'].substring(0,3))   {
                            this.setConfig(configResults.value[0])
                        }  
                        // overload stored config
                        this.workingConfig = configResults.value[0]
         
                        // MARK: Need to check to see if the changes will affect agreements.  This is on the approver
                        // items.   Pull all agreements on the orgcode.  Then update the college and 
                        // department approver data.
                    
                        
                    })
                })
                this.sRenderState = 'CONFIG-SAVED'
             },
             resetForm()    {

                //console.log("Settings:: resetForm fired")
                // reinitialize working config set
                this.workingConfig     =   {
                    'apply-college-code':       null,
                    'apply-dept-code':          null,
                    'apply-orgcode':            null,
                    'notice':                   "",
                    'title':                    "University of Illinois Urbana-Champaign",
                    'title-link':               "https://illinois.edu",
                    'suppress-cmdb-data':       false,
                    'suppress-photos':          false,
                    'barcode-type':             "Code128",
                    'tab_prefix':               "UN"
                 }
    
                this.sRenderState = 'SELECT-ORG'
            } ,
            // Function invoked when seleted config not in database
            // render edit page on the current data
            showForm() {
                this.sRenderState = 'SHOW-CONFIG'
            },
            // Function to update department code selections based on college code filters
            updateCollegeCode() {
                // filter departments based on whether the associated college code on the 
                // department is that selected
                this.aFilteredDeptCodes = this.aDeptCodes.filter ((item) => {
                    return item['college-code'] == this.workingConfig['apply-college-code']
                })
                this.aFilteredDeptCodes.sort((a, b) => {
                    // check department preferences for sorts
                    var codeA
                    var codeB
                    if(this.bSortByName) {
                        codeA = a['name']
                        codeB = b['name']
                    }
                    else {
                        codeA = a['dept-code']
                        codeB = b['dept-code']
                    }               
                    if (codeA < codeB) {
                        return -1
                    }
                    if (codeA > codeB) {
                        return 1
                    }
                    // names must be equal
                    return 0
                });
                this.configDisplay = this.makeConfigDisplay()
                this.bShowDeptSelector = true
             },
            // required select
            updateDeptCode() {
                // fires when user selects/multiselects a department code from list
                // filter division/orgs based on selected department code
                //
                this.aFilteredOrgCodes = this.aOrgCodes.filter ((item) => {
                    return item['dept-code'] == this.workingConfig['apply-dept-code']
                })
                /*
                for ( let key in this.aOrgCodes)  {
                    let item = this.aOrgCodes[key]
                    if(item['dept-code'] == this.workingConfig['apply-dept-code'])    {
                        this.aFilteredOrgCodes.push(item);  
                    }
                }
                */
                this.aFilteredOrgCodes.sort((a, b) => {
                    var codeA
                    var codeB
                    if(this.bSortByName) {
                        codeA = a['name']
                        codeB = b['name']
                    }
                    else {
                        var codeA = a['org']
                        var codeB = b['org']
                    }                        
                    if (codeA < codeB) {
                        return -1
                    }
                    if (codeA > codeB) {
                        return 1
                    }
                    // names must be equal
                    return 0
                });
                this.configDisplay = this.makeConfigDisplay()
                this.bShowOrgSelector = true
            },
            updateOrgCode()    {
                // placeholder if required
                this.configDisplay = this.makeConfigDisplay()
            },
            findCollegeName(code) {
                console.log("findCollegeName:: code: "+code)
                let extractedCollege = this.aCollegeCodes.filter( (item) => {
                    return item['code'] == code}
                )
                console.log("Extracted College: "+JSON.stringify(extractedCollege))
                if(extractedCollege.length > 0)
                    return extractedCollege[0].name
                else return ""
                
            },
            findDeptName(code) {
                console.log("findDeptName:: code: "+code)
                let extractedDept = this.aDeptCodes.filter( (item) => {
                    return item['dept-code'] == code}
                )
                console.log("Extracted Department: "+JSON.stringify(extractedDept))
                if(extractedDept.length > 0)
                    return extractedDept[0].name
                else return ""  
            },
            findDivisionName(code) {
                console.log("findDivisionName:: code: "+code)
                let extractedDiv = this.aOrgCodes.filter( (item) => {
                    return item['org'] == code}
                )
                console.log("Extracted Division: "+JSON.stringify(extractedDiv))
                if(extractedDiv.length > 0)
                    return extractedDiv[0].name
                else return ""   
            }
 
        }
    }
</script>

<style scoped>
    ._master_container {
        font-family:Monserrat, Avenir, Helvetica, Arial, sans-serif;
        min-height: 60vh;
        width: 95vw;
        margin:  0 0 0 10px;
        padding: 2px;
    }
    ._info {
        margin: 0;
        padding: 0;
    }
    ._org_selector {
        width: 100%;
        margin: 0;
        padding: 0;
    }
    ._display_container {  
        width: 95%;
        margin: 0;
        padding: 0;
    }
    ._row_box {
        width: 100%;
        min-height: 100px;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:#BFC9D9;
        text-align: left;
        margin: 0 15px 0 0;
        padding: 3px;
    }
    ._p_section {
        width: 100%;
        margin: 0;
        padding: 2px;
        font-size: 0.9em;
        font-weight: bold;
        text-align: left;
        color: #13294B;  
    }
    ._p_config {
        font-size: 0.9em;
        font-weight: bold;
        color: #FF5F05;
        text-align: center;
        margin: 0 auto;
        padding: 0;
    }
    ._p_red {
        width: 60%;
        margin: 0 auto;
        padding: 2px;
        font-size: 12pt;
        text-align: left;
        color: #FF5F05;  
    }

    ._h1 {
        font-size: 1.3em;
        color: #13294B;
        text-align: center;
        margin: 10px 0 0 0;
        padding: 2px;
    }
    ._h1_02 {
        width: 100%;
        font-size: 1.0em;
        text-align: center;
        color: #13294B;
        margin: 10px 0 15px 0;
        padding: 2px;   
    }
    ._p_info {
        width: 60%;
        margin: 2% auto 0;
        padding: 2px;
        font-size: 1.0em;
        text-align: left;
        color: #13294B;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    ._ul {
        width: 60%;
        margin: 2px auto 0;
        padding: 2px;
    }
    ._li {
        margin: 0 0 0 40px;
        padding: 2px;
        text-align: left;
        font-size: 0.85em;
        color: #13294B;
    }
    ._check {
        width: 25px;
        margin: 5px auto;
        padding: 0;
    }
    ._item_label {
        font-size: 0.8em;
        color: #13294B;
        padding: 2px;
        text-align: left;
    }
    ._select {
        width: 100%;
        margin: 3px 0;
        padding: 3px;
    }
    ._opt_list   {
        color: black;
        margin: 5px 0 5px;
        letter-spacing: 1px;
        font-weight: normal;
        font-size: 0.9em;
    }
    ._input  {
        width: 90%;
        overflow: hidden;
        background-color:#a4b9dc;
        margin: 0;
        padding: 0 5px;
        font-size: 0.9em;
        color: black;
    }
    ._textarea {
        width: 95%;
        max-width: 85%;
        background-color:#a4b9dc;
        margin: 0;
        padding: 10px;
    }
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-radius: 5px;
        border-width: 1px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    ._p_diminished_grn_2 {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: top 2px, bottom 0, left 2px, right 2px ;
    }
 
    /*  >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    /   RESPONSIVE DESIGN STYLING
    >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>/*
    /*  responsive design flex rows  4 major rows or 1 or 2 columns */
    ._row_container {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 5px 0 0 0;
        padding: 0;
    }
    /* override for buttons */
    ._button_container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
        margin: 6px 0;
        /*
        width: 50%;
        margin: 20px auto;
        */
    }
   
    /*  responive design columns - scol are subcolumns  */
    ._row:after {
        content: "";
        display: table;
        clear: both;
    }

    ._col_50 {
        float: left;
        width: 50%;
        margin: 0 10px 0 0;
        padding: 2px 10px;
    }
    ._col_75 {
        float: left;
        width: 75%;
        margin: 0 10px 0 0;
        padding: 2px 10px;
    }
    /*  sub columns - columns for items within a column*/
    ._scol_8 {
        float: left;
        width: 8%;
        margin-top: 2px;
    }
    ._scol_15 {
        float: left;
        width: 15%;
        margin-top: 5px;
    }
    ._scol_25 {
        float: left;
        width: 25%;
        margin-top: 5px;
    }
    ._scol_40 {
        float: left;
        width: 40%;
        margin-top: 5px;
    }
    ._scol_60 {
        float: left;
        width: 60%;
        margin-top: 5px;
    }
    ._scol_75 {
        float: left;
        width: 75%;
        margin-top: 5px;
    }
    ._scol_80 {
        float: left;
        width: 80%;
        margin-top: 2px;
    }
   

    @media screen and (max-width: 800px) {
        ._row_container  {
            flex-wrap: wrap;  
        }
        ._col_50  {
            width: 100%;
            margin-top: 0; 
        }
        .set_col_75 {
            width: 100%;
            margin-top: 0;
        }
        /*
        .set_scol_50 {
            width: 30%;
            margin-top: 0; 
        }
        */
    }
 
    @media screen and (max-width: 620px) {
        ._col_50 {
            width: 100%;
            margin-top: 0;  
        }
        ._col_75 {
            width: 100%;
            margin-top: 0;
        }
        ._scol_25  {
            width: 100%;
            margin-top: 0;
        }
        ._scol_40  {
            width: 100%;
            margin-top: 0;
        }
        ._scol_50  {
            width: 100%;
            margin-top: 0;
        }
        ._scol_60  {
            width: 100%;
            margin-top: 0;
        }
        ._scol_75  {
            width: 100%;
            margin-top: 0;
        }
    }
</style>